import {
  Divider,
  Option,
  Text,
  BaseOptionProps,
  Link,
  Heading,
  Avatar,
  useDesignLanguagePerClass,
  AllIconNames,
  DesignLanguage,
  useGetSelectedDesignLanguage,
} from '@afterpaytouch/core'
import { Trans, useTranslation } from '../../utils/convergence'
import React, { FunctionComponent, useEffect } from 'react'
import { Form } from '..'
import { Route } from '../../config/router'
import { IdentityDocument, getVerifyDocumentRoute, isCountryNeedsBiometricVerification } from '../../model'
import { useCountry, useConsumerSelector, useIsPartialVerified, useConsumerName } from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import styles from './styles.module.css'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { useAllowedDocumentsByCountry } from '../../utils/hooks'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'

interface Props {
  onSelect: (route: Route) => void
  showProfile?: boolean
  showHelpFooter?: boolean
}

type DesignLanguageIcon = Record<DesignLanguage, AllIconNames>

const icons: Record<IdentityDocument, AllIconNames | DesignLanguageIcon> = {
  licence: 'Car',
  medicare: {
    [DesignLanguage.Bronte]: 'Heartbeat',
    [DesignLanguage.Arcade]: 'CategoryHealthMedical',
  },
  passport: 'Globe',
}

export const VerifyIdentity: FunctionComponent<Props> = ({ onSelect, showProfile = false, showHelpFooter = true }) => {
  const { t } = useTranslation()
  const country = useCountry()
  const { headingSize, textSize, smallTextSize, sectionHeadingSize } = useCheckoutSizes()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const designLanguageStyles = useDesignLanguagePerClass(styles)
  const selectedDesignLanguage = useGetSelectedDesignLanguage()
  const { verifiedIdentityDocuments, email } = useConsumerSelector()
  const isPartialVerified = useIsPartialVerified()
  const startBiometricHardIdFlow = isCountryNeedsBiometricVerification(country)

  const consumerName = useConsumerName()

  const getPressedButtonEventName = (doc: IdentityDocument): string => {
    return {
      licence: TrackingEvent.PRESSED_HARDID_LANDING_PAGE_DRIVERS_LICENCE_BUTTON,
      medicare: TrackingEvent.PRESSED_HARDID_LANDING_PAGE_MEDICARE_BUTTON,
      passport: TrackingEvent.PRESSED_HARDID_LANDING_PAGE_PASSPORT_BUTTON,
    }[doc]
  }

  const documentLabel: Record<IdentityDocument, string> = {
    licence: t('verifyIdentity:document.licence'),
    medicare: t('verifyIdentity:document.medicare'),
    passport: t('verifyIdentity:document.passport'),
  }

  const getPropsByDocument = (doc: IdentityDocument): BaseOptionProps => ({
    icon: typeof icons[doc] === 'string' ? icons[doc] : icons[doc][selectedDesignLanguage],
    text: documentLabel[doc],
    onClick: (e) => {
      e.preventDefault()
      logEvent(getPressedButtonEventName(doc))
      onSelect(getVerifyDocumentRoute({ document: doc, country }))
    },
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const transformedAllowedDocument = useAllowedDocumentsByCountry(country).filter((doc) => !(verifiedIdentityDocuments?.includes(doc as any) ?? false))

  useEffect(() => {
    if (transformedAllowedDocument.length === 1 && !startBiometricHardIdFlow) {
      onSelect(getVerifyDocumentRoute({ document: transformedAllowedDocument[0], retry: isPartialVerified, country }))
    }
  }, [])

  return (
    <div>
      <div className={styles.pageHeadingWrapper}>
        <Heading size={headingSize}>{t('verifyIdentity:pageHeading')}</Heading>
      </div>
      <Form>
        {showProfile ? (
          <>
            <MaskedElement actionName='Verify Info'>
              <div className={designLanguageStyles.profileCard}>
                <Avatar size='M' src='' alt={consumerName} name={consumerName} title={consumerName} />
                <div className='info'>
                  <Text bold size={sectionHeadingSize}>
                    {consumerName}
                  </Text>
                  <Text size={smallTextSize} color='Gray50'>
                    {email}
                  </Text>
                </div>
              </div>
            </MaskedElement>
            <Text size={textSize}>
              <Trans components={{ strong: <strong /> }} i18nKey='verifyIdentity:description' />
            </Text>
          </>
        ) : (
          <>
            <Text size={textSize}>
              <Trans components={{ strong: <strong /> }} i18nKey='verifyIdentity:description' />
            </Text>
            <Divider kind='Dashed' />
            <Text size={textSize} bold>
              {t('verifyIdentity:chooseDocument')}
            </Text>
          </>
        )}

        <div className={styles.documentOptions}>
          {transformedAllowedDocument.map((doc) => {
            const key = `verify-identity-${doc}`
            return <Option key={key} testNameSpace={key} {...getPropsByDocument(doc)} />
          })}
        </div>

        {showHelpFooter && (
          <Text size={textSize}>
            <Trans
              i18nKey='verifyIdentity:help'
              components={{
                a: (
                  <Link
                    key='description-link'
                    target='_blank'
                    kind='Underline'
                    href={t('common:identityHelpUrl')}
                    testNameSpace='verifyIdentity'
                    onClick={() => {
                      logEvent(TrackingEvent.PRESSED_HARDID_LANDING_PAGE_HELP_LINK)
                    }}
                  />
                ),
              }}
            />
          </Text>
        )}
      </Form>
    </div>
  )
}
